import { takeUntil } from 'rxjs/operators';
import { ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { TabsModule } from 'primeng/tabs';
import { CardModule } from 'primeng/card';
import { Subject, forkJoin, of } from 'rxjs';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { DialogService } from 'primeng/dynamicdialog';
import { BackButton } from '@components/ui/back-button/back-button';
import { Loading } from '@components/ui/loading/loading';
import { LockPermissionsComponent } from '../lock-permissions/lock-permissions.component';
import { UserRolesListComponent } from './user-roles-list/user-roles-list.component';
import { UserListComponent } from './user-list/user-list.component';
import { UserManagementModalsComponent } from './user-management-modals/user-management-modals.component';
import { UserRoleDetailDialog } from '@app/pages/user-management/detail/user-role-detail-dialog/user-role-detail-dialog';
import { ServerTranslateService } from '@services/server-translate.service';
import { UserManagementService, User, RoleWithUsers } from './user-management.service';
import { GenericSelectorConfig } from '../../Ui-Kit/generic-selector/generic-selector.models';

@Component({
  selector: 'app-user-management-page',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    ButtonModule,
    FormsModule,
    TabsModule,
    CardModule,
    ToastModule,
    BackButton,
    Loading,
    LockPermissionsComponent,
    UserRolesListComponent,
    UserListComponent,
    UserManagementModalsComponent,
  ],
  providers: [MessageService, DialogService, UserManagementService],
  templateUrl: './user-management-page.html',
  styleUrl: './user-management-page.scss',
})
export class UserManagementPageComponent implements OnInit, OnDestroy {
  @ViewChild('modals') modals!: UserManagementModalsComponent;
  @ViewChild(UserListComponent) userListComponent!: UserListComponent;

  users: User[] = [];
  rolesData: any[] = [];
  roleUserCounts: { [roleName: string]: { total: number; users: User[] } } = {};
  rolesWithUsers: RoleWithUsers[] = [];

  roleOptions: any[] = [];

  activeTab: string = '0';
  selectedRole: string | null = null;
  loading = true;
  isLoading: boolean = false;
  isSmallScreen: boolean = false;
  isSaving: boolean = false;

  pageSize: number = 10;
  totalRecords: number = 0;
  filters = {};

  loadingStates = {
    roles: false,
  };

  private destroy$ = new Subject<void>();

  userSelectorConfig!: GenericSelectorConfig<any>;
  orgSelectorConfig!: GenericSelectorConfig<any>;
  roleSelectorConfig!: GenericSelectorConfig<any>;

  constructor(
    private cdr: ChangeDetectorRef,
    private router: Router,
    private translate: TranslateService,
    private dialogService: DialogService,
    private serverTranslate: ServerTranslateService,
    private userManagementService: UserManagementService,
  ) {}

  ngOnInit(): void {
    this.initSelectorConfigs();
    this.loadDictionaries();
    this.loadUsers();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initSelectorConfigs(): void {
    this.userSelectorConfig = {
      searchField: 'fullName',
      displayField: 'fullName',
      idField: 'id',
      getItems: (params) => this.userManagementService.getUsersForSelector(params),
      getTotalCount: (params) => this.userManagementService.getUsersTotalCount(params),
      getItemById: (id) => this.userManagementService.getUserById(id),
    };

    this.orgSelectorConfig = {
      searchField: 'shortName',
      displayField: 'shortName',
      idField: 'id',
      getItems: (params) => this.userManagementService.getOrganizationsForSelector(params),
      getTotalCount: (params) => this.userManagementService.getOrganizationsTotalCount(params),
      getItemById: (id) => this.userManagementService.getOrganizationById(id),
    };

    this.roleSelectorConfig = {
      searchField: 'normalizedName',
      displayField: 'name',
      idField: 'normalizedName',
      getItems: (params) => this.userManagementService.getRolesForSelector(params),
      getTotalCount: (params) => of(100),

      formatLabel: (item) => {
        const result = this.serverTranslate.translate(item, 'names');
        if (Array.isArray(result)) {
          return result[0] || item.name || '';
        }
        return result || item.name || '';
      },
    };
  }

  private loadDictionaries(): void {
    this.loadingStates.roles = true;

    forkJoin({
      roles: this.userManagementService.getRoles(),
    }).pipe(takeUntil(this.destroy$)).subscribe({
      next: (data) => {
        this.roleOptions = data.roles || [];
        this.rolesData = this.roleOptions;

        this.loadingStates.roles = false;

        this.roleOptions.forEach((role) => {
          if (role.name) this.loadUsersInRole(role.name);
        });

        this.cdr.detectChanges();
      },
      error: (err) => {
        console.error('Error loading dictionaries', err);
        this.loadingStates.roles = false;
      },
    });
  }

  loadUsers(): void {
    this.isSaving = true;
    this.userManagementService.getUsers(this.filters, 1000, 0).subscribe({
      next: (data) => {
        this.users = data.users;
        this.totalRecords = data.total;

        this.loading = false;
        this.isSaving = false;
        this.cdr.detectChanges();
      },
      error: (error) => {
        console.error('Error loading users:', error);
        this.loading = false;
        this.isSaving = false;
        this.cdr.detectChanges();
      },
    });
  }

  onUserSaved(): void {
    this.loadUsers();
    if (this.userListComponent) {
      this.userListComponent.resetTable();
    }
  }

  onTabChange(event: any): void {
    const tabValue = event?.index ?? event?.value ?? event;
    if ((tabValue === 1 || tabValue === '1' || this.activeTab === '1') && this.rolesWithUsers.length === 0) {
      this.loadRolesWithUsers();
    }
  }

  loadRolesWithUsers(): void {
    this.userManagementService.getRolesWithUsers().pipe(takeUntil(this.destroy$)).subscribe({
      next: (response) => {
        this.rolesWithUsers = response || [];
        this.mapRolesWithUsersData(response);
        this.cdr.detectChanges();
      },
      error: (error) => console.error('Error loading roles with users:', error),
    });
  }

  private mapRolesWithUsersData(response: RoleWithUsers[]): void {
    this.roleUserCounts = {};
    response.forEach((role) => {
      const translatedRoleName = this.serverTranslate.translate(role.roleName, 'names');
      const roleName = Array.isArray(translatedRoleName) ? translatedRoleName[0] : translatedRoleName || 'Unknown Role';

      this.roleUserCounts[roleName] = {
        total: role.users.length,
        users: role.users.map((u: any) => ({
          ...u,
          username: u.fullName,
          status: 'active',
        })),
      };
    });
  }

  loadUsersInRole(roleName: string): void {
    this.userManagementService.getUsersInRole(roleName).pipe(takeUntil(this.destroy$)).subscribe({
      next: (response) => {
        if (Array.isArray(response)) {
          if (!this.roleUserCounts[roleName]) {
            this.roleUserCounts[roleName] = { total: 0, users: [] };
          }
          this.roleUserCounts[roleName].total = response.length;
          this.roleUserCounts[roleName].users = response;
          this.cdr.detectChanges();
        }
      },
    });
  }

  selectRole(roleId: string): void {
    this.selectedRole = this.selectedRole === roleId ? null : roleId;
  }

  showUserInRole(role: any, $event: MouseEvent): void {
    $event.stopPropagation();
    this.dialogService.open(UserRoleDetailDialog, {
      draggable: false,
      resizable: false,
      header: this.translate.instant('USERS_IN_ROLE') + `: ${this.serverTranslate.translate(role, 'normalizedName')}`,
      width: '600px',
      dismissableMask: true,
      data: {
        role,
        users: this.roleUserCounts[role.name]?.users || [],
      },
    });
  }

  applyFilters(): void {
    this.loadUsers();
  }

  clearFilters(): void {
    this.filters = {};
    this.loadUsers();
  }

  onPageSizeChange(newPageSize: number): void {
    this.pageSize = newPageSize;
    this.loadUsers();
  }

  addIndividualUser(): void {
    this.modals.openAddIndividual();
  }

  addLegalEntityUser(): void {
    this.modals.openAddLegal();
  }

  importIndividualUsers(): void {
    this.modals.importIndividual();
  }
  importLegalEntityUsers(): void {
    this.modals.importLegal();
  }

  editUser(user: User): void {
    this.modals.openEdit(user);
  }
}
